import React, { useState } from 'react';
import { ShoppingCart, Check, Info, Wrench, Package } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const ProductCardNew = ({ product, onAddToCart }) => {
  const [withInstallation, setWithInstallation] = useState(false);

  const selectedPrice = withInstallation
    ? product.priceWithInstall
    : product.priceWithoutInstall;

  return (
    <Card
      data-testid={`product-card-${product.code}`}
      className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-2 border-transparent hover:border-[#00BCD4]"
    >
      <div className="relative overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-300"
        />
        <Badge className="absolute top-3 right-3 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white border-none font-bold">
          {product.code}
        </Badge>
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-700">
          {product.category}
        </div>
      </div>
      <CardHeader>
        <h3 className="font-bold text-lg text-gray-900 group-hover:text-[#00BCD4] transition-colors">
          {product.name}
        </h3>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600 text-sm mb-4">{product.description}</p>

        {/* Installation toggle - segmented control */}
        <div className="grid grid-cols-2 gap-2 mb-4 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            data-testid={`product-${product.code}-without-install-btn`}
            onClick={() => setWithInstallation(false)}
            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-all ${
              !withInstallation
                ? 'bg-white text-[#00BCD4] shadow'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Package size={16} />
            Sans pose
          </button>
          <button
            type="button"
            data-testid={`product-${product.code}-with-install-btn`}
            onClick={() => setWithInstallation(true)}
            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-all ${
              withInstallation
                ? 'bg-white text-[#7CB342] shadow'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Wrench size={16} />
            Avec pose
          </button>
        </div>

        {/* Price display */}
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <span
              data-testid={`product-${product.code}-price`}
              className="text-4xl font-bold bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent"
            >
              {selectedPrice}
            </span>
            <span className="text-xl font-semibold text-gray-600">CHF</span>
          </div>
          <div className="text-xs text-gray-500">
            {withInstallation ? '✓ Pose / installation incluse' : 'Kit seul (sans pose)'}
          </div>
          <div className="text-[11px] text-gray-400 italic">
            Prix net, TVA non applicable
          </div>
        </div>

        {/* Features list */}
        <div className="mt-4 space-y-2">
          {product.features.slice(0, 3).map((feature, index) => (
            <div key={index} className="flex items-start gap-2 text-sm text-gray-600">
              <Check size={16} className="text-[#7CB342] flex-shrink-0 mt-0.5" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button
          data-testid={`product-${product.code}-add-cart-btn`}
          onClick={() => onAddToCart(product, withInstallation)}
          className="w-full bg-gradient-to-r from-[#7CB342] to-[#00BCD4] hover:from-[#689F38] hover:to-[#0097A7] text-white font-semibold py-3 transition-all shadow-md hover:shadow-lg"
        >
          <ShoppingCart size={18} className="mr-2" />
          Commander
        </Button>
        <Button
          data-testid={`product-${product.code}-quote-btn`}
          variant="outline"
          className="w-full border-[#00BCD4] text-[#00BCD4] hover:bg-[#00BCD4]/10"
          onClick={() => {
            const subject = `Demande de devis ${product.code} - ${product.name}`;
            const price = withInstallation ? product.priceWithInstall : product.priceWithoutInstall;
            const prestation = withInstallation ? 'avec pose' : 'sans pose';
            const message = `Bonjour,\n\nJe souhaite recevoir un devis pour le kit ${product.code} (${product.name}) ${prestation} (${price} CHF).\n\nVéhicule / informations complémentaires :\n\n\nMerci.`;
            const params = new URLSearchParams({ subject, message });
            window.location.href = `/contact?${params.toString()}`;
          }}
        >
          <Info size={18} className="mr-2" />
          Demander un devis
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProductCardNew;
